import { Check, ExternalLink } from "lucide-react";
import SEO_DATA from "@/content/seo-data.json";
import { GF_URL, VARIANT, VIP_URL } from "@/constants/links";

const isVip = VARIANT === "vip";
const content = SEO_DATA[isVip ? "vip" : "standard"];
const standardValue = (value) =>
  isVip
    ? value
    : value
        .replaceAll("Standard", "Guardian Family")
        .replace("prezzo indicato nello store", "3,89 € al mese");

export const SeoContent = () => (
  <>
    <section
      id="confronta"
      aria-labelledby="confronta-title"
      className="scroll-mt-24 bg-softpurple/45 py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex rounded-full bg-violet px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-white">
            Stesso ecosistema, due offerte
          </span>
          <h2
            id="confronta-title"
            className="mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl lg:text-5xl"
          >
            {isVip
              ? "Confronta Guardian Family Standard e Guardian Family VIP"
              : "Confronta Guardian Family e Guardian Family VIP"}
          </h2>
          <p className="mt-5 text-base font-medium leading-relaxed text-ink/75 sm:text-lg">
            {isVip
              ? "Guardian Family è il brand e l’ecosistema comune. Standard è l’offerta principale; VIP è l’offerta avanzata con account e dashboard dedicati."
              : "Guardian Family è l’app principale su Google Play; Guardian Family VIP è il prodotto avanzato separato con account e dashboard dedicati."}
          </p>
        </div>

        <div
          aria-labelledby="confronta-title"
          className="mt-12 overflow-x-auto rounded-[2rem] border-2 border-ink/10 bg-white shadow-xl shadow-violet/10"
          role="region"
          tabIndex="0"
        >
          <table className="w-full min-w-[720px] border-collapse text-left">
            <caption className="sr-only">
              {isVip
                ? "Differenze verificate tra Guardian Family Standard e Guardian Family VIP"
                : "Differenze verificate tra Guardian Family e Guardian Family VIP"}
            </caption>
            <thead className="bg-ink text-white">
              <tr>
                <th className="px-6 py-5 font-display text-sm font-black" scope="col">
                  Caratteristica
                </th>
                <th className="px-6 py-5 font-display text-sm font-black" scope="col">
                  {isVip ? "Guardian Family Standard" : "Guardian Family"}
                </th>
                <th className="px-6 py-5 font-display text-sm font-black" scope="col">
                  Guardian Family VIP
                </th>
              </tr>
            </thead>
            <tbody>
              {SEO_DATA.comparison.map((row) => (
                <tr className="border-t border-ink/10 align-top" key={row.label}>
                  <th className="px-6 py-5 font-bold text-ink" scope="row">
                    {row.label}
                  </th>
                  <td className="px-6 py-5 font-medium leading-relaxed text-ink/75">
                    {standardValue(row.standard)}
                  </td>
                  <td className="px-6 py-5 font-medium leading-relaxed text-ink/75">
                    {row.vip}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm font-bold">
          <a
            className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 bg-white px-5 py-3 text-ink transition-colors hover:border-violet hover:text-violet"
            href={isVip ? GF_URL : VIP_URL}
          >
            {isVip ? "Vai a Guardian Family Standard" : "Approfondisci Guardian Family VIP"}
            <ExternalLink aria-hidden="true" size={16} />
          </a>
        </div>
      </div>
    </section>

    <section
      id="domande-frequenti"
      aria-labelledby="faq-title"
      className="scroll-mt-24 bg-white py-24 md:py-32"
    >
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <span className="inline-flex rounded-full bg-yellow px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-ink">
            Risposte dirette
          </span>
          <h2
            id="faq-title"
            className="mt-5 font-display text-3xl font-black tracking-tight text-ink sm:text-4xl"
          >
            Domande frequenti su {content.shortName}
          </h2>
          <p className="mt-5 text-base font-medium leading-relaxed text-ink/75">
            {content.intro}
          </p>
          <ul className="mt-7 space-y-3">
            {content.features.map((feature) => (
              <li className="flex items-start gap-3 font-medium text-ink/80" key={feature}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet text-white">
                  <Check aria-hidden="true" size={14} strokeWidth={3} />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4">
          {content.faq.map((item) => (
            <details
              className="group rounded-3xl border-2 border-ink/10 bg-background px-6 py-5 open:border-violet/40 open:bg-softpurple/30"
              key={item.question}
            >
              <summary className="cursor-pointer pr-8 font-display text-lg font-black text-ink">
                {item.question}
              </summary>
              <p className="mt-4 font-medium leading-relaxed text-ink/75">{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </>
);
